import { useRef, useState, useEffect } from 'react';
import { useInView } from 'framer-motion';

const ENGINES = [
  { name: 'ChatGPT', icon: 'GPT' },
  { name: 'Claude', icon: 'CL' },
  { name: 'Gemini', icon: 'GEM' },
  { name: 'Perplexity', icon: 'PPX' },
  { name: 'Google AI', icon: 'GAI' },
  { name: 'Copilot', icon: 'COP' },
  { name: 'Grok', icon: 'GRK' },
  { name: 'Siri', icon: 'SRI' },
  { name: 'Alexa', icon: 'ALX' },
];

/* ══════════════════════════════════════════
   SECTION 7 — THE ENGINE
   3 x 3 command-center grid that lights up
   sequentially like mission control booting.
   ══════════════════════════════════════════ */
export default function TheEngine() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [activeCount, setActiveCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!isInView || started.current) return;
    started.current = true;
    const timers = [];
    for (let i = 1; i <= 9; i++) {
      timers.push(setTimeout(() => setActiveCount(i), i * 400));
    }
    /* After last engine activates, settle all to "active" */
    timers.push(setTimeout(() => setActiveCount(10), 9 * 400 + 600));
    return () => timers.forEach(clearTimeout);
  }, [isInView]);

  return (
    <section ref={ref} className="py-20 px-6" data-testid="the-engine-section">
      {/* Title */}
      <p
        className="text-center mb-10"
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '11px',
          color: '#B8985A',
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
        }}
        data-testid="engine-title"
      >
        OUR AI MONITORING ENGINE
      </p>

      {/* ── 3 x 3 Grid ── */}
      <div
        className="grid grid-cols-3 mx-auto"
        style={{ maxWidth: '600px', gap: '12px' }}
        data-testid="engine-grid"
      >
        {ENGINES.map((engine, i) => {
          const isDormant = i >= activeCount;
          const isActivating = activeCount <= 9 && i === activeCount - 1;

          return (
            <div
              key={engine.name}
              className="flex flex-col items-center justify-center transition-all duration-500"
              style={{
                height: '80px',
                backgroundColor: isDormant
                  ? 'rgba(240,235,227,0.02)'
                  : 'rgba(240,235,227,0.03)',
                border: `1px solid ${
                  isActivating
                    ? 'rgba(184,152,90,0.5)'
                    : isDormant
                    ? 'rgba(240,235,227,0.04)'
                    : 'rgba(184,152,90,0.3)'
                }`,
                borderRadius: '2px',
                boxShadow: isActivating
                  ? '0 0 20px rgba(184,152,90,0.15)'
                  : 'none',
              }}
              data-testid={`engine-card-${i}`}
            >
              <span
                className="text-[18px] font-light mb-1 transition-all duration-500"
                style={{
                  color: isDormant ? 'rgba(240,235,227,0.12)' : '#F0EBE3',
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.1em',
                }}
              >
                {engine.icon}
              </span>
              <span
                className="text-[10px] transition-all duration-500"
                style={{
                  color: isDormant
                    ? 'rgba(240,235,227,0.12)'
                    : 'rgba(240,235,227,0.5)',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {engine.name}
              </span>
              {!isDormant && (
                <div className="flex items-center gap-1 mt-1">
                  <div
                    className="w-1 h-1 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: isActivating ? '#B8985A' : '#5A9A7A',
                    }}
                  />
                  <span
                    className="text-[8px] transition-all duration-300"
                    style={{
                      color: isActivating
                        ? '#B8985A'
                        : 'rgba(240,235,227,0.4)',
                    }}
                  >
                    {isActivating ? 'Monitoring' : 'Active'}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary text */}
      <div className="text-center mt-10">
        <p
          style={{
            fontSize: '14px',
            color: '#F0EBE3',
            fontFamily: "'DM Sans', sans-serif",
          }}
          data-testid="engine-summary"
        >
          Every platform. Every query. Every signal. Monitored and optimized
          weekly.
        </p>
        <p
          className="mt-3"
          style={{ fontSize: '12px', color: 'rgba(240,235,227,0.4)' }}
        >
          9 engines &bull; 14 queries per scan &bull; 80+ optimization signals
        </p>
      </div>

      {/* Monitoring promise card */}
      <div
        className="mx-auto mt-8 px-6 py-4"
        style={{
          maxWidth: '520px',
          border: '1px solid rgba(240,235,227,0.08)',
          borderRadius: '2px',
          backgroundColor: 'rgba(240,235,227,0.03)',
        }}
        data-testid="engine-monitoring-card"
      >
        <p
          className="text-center"
          style={{
            fontSize: '13px',
            color: 'rgba(240,235,227,0.6)',
            fontFamily: "'DM Sans', sans-serif",
            lineHeight: 1.7,
          }}
        >
          Your business is rescanned every week. When AI changes who it
          mentions, you'll know within hours — not months.
        </p>
      </div>

      {/* Bridge text — verbal transition to Act 3 */}
      <p
        className="text-center mt-16"
        style={{
          fontSize: '13px',
          color: '#F0EBE3',
          fontFamily: "'DM Sans', sans-serif",
        }}
        data-testid="engine-bridge-text"
      >
        Now let us show you what we build.
      </p>
    </section>
  );
}
